import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useReactiveVar } from '@apollo/client'
import { Modal, ModalProvider } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { fromGlobalId } from 'utils/transformations/graphql'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import { OrderData } from '..'
import { REVIEW_PAGE_TEST_IDS } from '../../review.ids'
import { orderReviewPartialErrorVar } from '../../review.store'
import {
  DetailLoading,
  EditableHeaderDetails,
  EditableHeaderTestIds,
} from './EditableHeaderDetails'
import { ReadonlyHeaderDetails } from './ReadonlyHeaderDetails'
import StatusDropdown from './components/StatusDropdown/StatusDropdown'
import {
  useUpdateHeaderDates,
  useUpdateOrderInfo,
  useUpdateOrderNumber,
  useUpdateRetailerCode,
} from './header.mutations'
import * as UI from './header.ui'
import { OrderErrors } from 'features/Orders/Review/review.queries'
import { StatusPill } from 'features/Orders/components'
import { pillTextByStatus } from 'features/Orders/utils/helpers'

const StyledDetailContainer = styled(UI.DetailContainer)`
  align-items: center;
`

const EditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`

const WarningMessage = styled.p`
  margin: 0;
`

export enum HeaderTestIds {
  OrderNumberItem = 'header-order-number-item',
  RetailerNameItem = 'header-retailer-name-item',
  PriceTypeItem = 'header-price-type-item',
  StatusItem = 'header-status-item',
  StatusDropdown = 'header-status-dropdown',
  LinesheetNameItem = 'header-linesheet-name-item',
}

type Props = {
  order?: OrderData
  error?: OrderErrors
  openAssignRetailerModal?: () => void
  isReadOnly?: boolean
  isFetching?: boolean
  showShadow?: boolean
}

export type EditedFields = {
  orderReferenceNumber: string
  retailerCode: string
  startDate?: Date
  endDate?: Date
  priceType: string
}

const PriceTypeWarningModal = ({
  confirmUpdatePriceType,
  isPriceTypeWarningModalOpen,
  setIsPriceTypeWarningModalOpen,
}: {
  confirmUpdatePriceType: () => void
  isPriceTypeWarningModalOpen: boolean
  setIsPriceTypeWarningModalOpen: Dispatch<SetStateAction<boolean>>
}) => {
  const Portal = usePortal()
  return (
    <Portal>
      <ModalProvider>
        <Modal
          title={{ text: 'Changing Price Type', iconName: 'warning' }}
          size={'small'}
          isOpen={isPriceTypeWarningModalOpen}
          onClose={() => setIsPriceTypeWarningModalOpen(false)}
        >
          <Modal.Content>
            <WarningMessage>
              Changing the price type will assign this price type to any values
              previously added for taxes and shipping fees. It will also reset
              any discounts to the default on the linesheet. Please review these
              fields to ensure the information is still correct.
            </WarningMessage>
          </Modal.Content>
          <Modal.Actions
            primaryButton={{
              text: 'Okay',
              handler: () => confirmUpdatePriceType(),
            }}
          />
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

export const Header = ({
  order,
  error,
  openAssignRetailerModal,
  isReadOnly,
  isFetching,
  showShadow = false,
}: Props) => {
  const { onUpdate } = useUpdateOrderInfo()
  const { onUpdateOrderDeliveryInfo } = useUpdateOrderNumber()
  const { onUpdateRetailerCode } = useUpdateRetailerCode()
  const { onUpdateHeaderDates } = useUpdateHeaderDates()

  const { renderSuccessToast } = useRenderToasters()
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)

  const [isEditMode, setEditMode] = useState(false)
  const [editedFields, setEditedFields] = useState<EditedFields>({
    orderReferenceNumber: '',
    retailerCode: '',
    startDate: undefined,
    endDate: undefined,
    priceType: '',
  })
  const [isPriceTypeWarningModalOpen, setIsPriceTypeWarningModalOpen] =
    useState(false)

  const showStatusDropdown = !isReadOnly && order?.status

  const isTherePriceTypeError = useReactiveVar(
    orderReviewPartialErrorVar,
  )?.priceType

  const initializeState = useCallback((orderInput: typeof order) => {
    setEditedFields({
      ...editedFields,
      orderReferenceNumber: orderInput?.referenceNumber || '',
    })
  }, [])

  useEffect(() => {
    if (order) {
      initializeState(order)
    }
  }, [order, initializeState])

  const arePriceValuesZero =
    parseInt(order?.tax?.amountString || '0') === 0 &&
    parseInt(order?.shipping?.priceString || '0') === 0

  const handleSaveBtnClick = () => {
    if (isEditMode) {
      if (editedFields.priceType && !arePriceValuesZero) {
        setIsPriceTypeWarningModalOpen(true)
      } else {
        saveChanges()
        setEditMode(false)
      }
    } else {
      setEditMode(true)
    }
  }

  const saveChanges = () => {
    const mutations = []
    if (editedFields.orderReferenceNumber.trim() !== order?.referenceNumber) {
      const newOrderReferenceNumber =
        editedFields.orderReferenceNumber === ''
          ? fromGlobalId(order!.id).id!
          : editedFields.orderReferenceNumber.trim()
      mutations.push(
        onUpdateOrderDeliveryInfo(
          {
            purchaseOrderCode: newOrderReferenceNumber,
          },
          order?.id || null,
        ),
      )
    }
    if (editedFields.retailerCode) {
      mutations.push(
        onUpdateRetailerCode({
          customerCodes: [
            {
              otherAccountId: order?.retailerProfile?.id || '',
              code: editedFields.retailerCode,
            },
          ],
        }),
      )
    }

    if (editedFields.priceType) {
      mutations.push(
        onUpdate({
          orderIds: [order?.id!],
          orderDetails: { priceTypeId: editedFields.priceType },
        }),
      )
    }

    if (editedFields.startDate || editedFields.endDate) {
      mutations.push(
        onUpdateHeaderDates({
          id: order?.id || '',
          ...(editedFields.startDate && {
            deliveryWindowStart: editedFields.startDate
              ?.toISOString()
              .substring(0, 10),
          }),
          ...(editedFields.endDate && {
            deliveryWindowEnd: editedFields.endDate
              ?.toISOString()
              .substring(0, 10),
          }),
        }),
      )
    }

    if (mutations.length) {
      Promise.allSettled(mutations).then(() =>
        renderSuccessToast({
          title: 'Success',
          description: 'Order updated successfully.',
        }),
      )
    }
  }

  const confirmUpdatePriceType = () => {
    saveChanges()
    setIsPriceTypeWarningModalOpen(false)
    setEditMode(false)
  }

  return (
    <div
      data-testid={REVIEW_PAGE_TEST_IDS.OrderHeaderDetails}
      style={{
        width: '100%',
        boxShadow: showShadow ? '0px 5px 3px -2px rgba(0,0,0,0.3)' : 'none',
        transition: 'box-shadow 0.2s ease-in-out',
        paddingBottom: '8px',
        zIndex: 2,
      }}
    >
      <StyledDetailContainer>
        {isEditMode && !isReadOnly && isAccountTypeBrand ? (
          <EditableHeaderDetails
            order={order}
            editedFields={editedFields}
            setEditedFields={setEditedFields}
            onHandleSaveBtnClick={handleSaveBtnClick}
          />
        ) : (
          <ReadonlyHeaderDetails
            order={order}
            isFetching={isFetching}
            openAssignRetailerModal={openAssignRetailerModal}
            error={error}
            onHandleSaveBtnClick={handleSaveBtnClick}
          />
        )}
        <EditWrapper>
          <div data-testid={EditableHeaderTestIds.StatusItem}>
            {isFetching && <DetailLoading />}
            {!isFetching && !!order?.status && (
              <StatusPill
                value={`Order Status: ${pillTextByStatus[order?.status]}`}
                status={order?.status}
              />
            )}
          </div>
          {showStatusDropdown && !isTherePriceTypeError && (
            <StatusDropdown
              data-testid={HeaderTestIds.StatusDropdown}
              order={order}
              disabled={isEditMode}
            />
          )}
          {showStatusDropdown && isTherePriceTypeError && (
            <UI.StyledTooltip
              content="Correct errors to move forward."
              defaultPlacement="left"
              variant="error"
            >
              <StatusDropdown
                data-testid={HeaderTestIds.StatusDropdown}
                order={order}
                disabled={isEditMode}
              />
            </UI.StyledTooltip>
          )}
        </EditWrapper>
      </StyledDetailContainer>
      <PriceTypeWarningModal
        confirmUpdatePriceType={confirmUpdatePriceType}
        isPriceTypeWarningModalOpen={isPriceTypeWarningModalOpen}
        setIsPriceTypeWarningModalOpen={setIsPriceTypeWarningModalOpen}
      />
    </div>
  )
}
